import type { TokenStore } from "@/domains/tokens/token-store"

/**
 * Input for price range calculation strategies
 * Contains all observable values needed for calculations
 */
export interface PriceRangeCalculationInput {
  minPrice: string | undefined
  maxPrice: string | undefined
  inputMode: "price" | "percentage"
  fullRange: boolean
  currentPrice: number | undefined
  currentTick: number | undefined
  tickSpacing: number | undefined
  token0: TokenStore | undefined
  token1: TokenStore | undefined
}

/**
 * Result of price range calculation
 * Contains all computed values in a single atomic result
 */
export interface PriceRangeCalculationResult {
  ticks: [number | undefined, number | undefined]
  pricesAtTicks: [string, string]
  priceDifferences: { min: number | undefined; max: number | undefined }
  ticksAtLimit: [boolean, boolean]
  minDisplayValue: string
  maxDisplayValue: string
  hasValidRange: boolean
}

/**
 * Strategy interface for price range calculations
 * Each strategy (Full Range, Custom Range) implements this interface
 */
export interface IPriceRangeCalculationStrategy {
  /**
   * Calculate all price range values in a single atomic operation
   * @param input - All observable values needed for calculation
   * @returns Complete calculation result with all computed values
   */
  calculate(input: PriceRangeCalculationInput): PriceRangeCalculationResult
}

/**
 * Default empty result for when pool data is unavailable
 */
export const DEFAULT_PRICE_RANGE_RESULT: PriceRangeCalculationResult = {
  ticks: [undefined, undefined],
  pricesAtTicks: ["", ""],
  priceDifferences: { min: undefined, max: undefined },
  ticksAtLimit: [false, false],
  minDisplayValue: "",
  maxDisplayValue: "",
  hasValidRange: false,
}
